import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Brain,
  MessageSquare,
  Activity,
  Layers,
  Database,
  Clock,
  TrendingUp,
  Eye,
  Shield,
  Zap,
  ChevronDown,
  ChevronRight,
  FileJson,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Badge } from '@personic/shared'
import { useAdminProfile } from '@/hooks/useAdminProfiles'
import { PersonalityRadar } from '@/components/mindmetal/PersonalityRadar'
import { SignalGauge } from '@/components/mindmetal/SignalGauge'
import { PreferencesList } from '@/components/mindmetal/PreferencesList'
import { CoverageIndicator } from '@/components/mindmetal/CoverageIndicator'
import type { BigFivePersonality, TrackedValue } from '@personic/shared'

type ProfileTab = 'personality' | 'preferences' | 'domains' | 'raw'

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
}

export default function ProfileDetail() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<ProfileTab>('personality')

  const { data: profile, isLoading, error } = useAdminProfile(id || null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-600" />
          <span className="text-warm-500 text-sm">Loading profile...</span>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <p className="text-red-700">Failed to load profile details.</p>
          <Link
            to="/employee/mindmetal/profiles"
            className="text-sm text-copper-600 hover:text-copper-700 mt-2 inline-block"
          >
            Back to Profile Browser
          </Link>
        </CardContent>
      </Card>
    )
  }

  // Extract structured signals from universal tier
  const universal = profile.signals_universal || {}
  const personality: BigFivePersonality = universal.personality || {}
  const commStyle = universal.communication_style || {}
  const cogStyle = universal.cognitive_style || {}
  const behavioral = profile.signals_behavioral || {}
  const preferences = behavioral.preferences || behavioral

  const TABS: { key: ProfileTab; label: string; icon: React.ElementType }[] = [
    { key: 'personality', label: 'Personality', icon: Brain },
    { key: 'preferences', label: 'Preferences', icon: MessageSquare },
    { key: 'domains', label: 'Domains', icon: Layers },
    { key: 'raw', label: 'Raw Data', icon: FileJson },
  ]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <motion.nav
        {...fadeUp}
        transition={{ duration: 0.3 }}
        className="flex items-center gap-2 text-sm"
      >
        <Link
          to="/employee/mindmetal/profiles"
          className="flex items-center gap-1.5 text-warm-500 hover:text-copper-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Profile Browser
        </Link>
        <span className="text-warm-300">/</span>
        <span className="text-midnight-800 font-medium">{profile.canonical_id}</span>
      </motion.nav>

      {/* Profile Header Card */}
      <motion.div
        {...fadeUp}
        transition={{ duration: 0.4, delay: 0.05 }}
      >
        <Card className="overflow-hidden">
          {/* Gradient accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-copper-400 via-copper-500 to-copper-600" />

          <div className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              {/* Left: Identity */}
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-copper-100 to-copper-200 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-7 h-7 text-copper-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-display font-bold text-midnight-900">
                    {profile.canonical_id}
                  </h1>
                  <p className="text-warm-500 text-sm font-mono mt-0.5">
                    {profile.profile_id}
                  </p>
                  {profile.tenant_name && (
                    <p className="text-warm-500 text-sm mt-1">
                      Tenant: <span className="text-midnight-700">{profile.tenant_name}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Right: Status indicators */}
              <div className="flex flex-wrap items-center gap-3">
                <CoverageIndicator
                  level={profile.coverage_level as any}
                  observationCount={profile.total_observation_count}
                />
                {profile.stability_indicator && (
                  <Badge
                    variant={
                      profile.stability_indicator === 'stable'
                        ? 'sage'
                        : profile.stability_indicator === 'stabilizing'
                          ? 'warning'
                          : 'default'
                    }
                  >
                    {profile.stability_indicator}
                  </Badge>
                )}
                {!profile.is_active && (
                  <Badge variant="default">Inactive</Badge>
                )}
              </div>
            </div>

            {/* Metadata grid */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MetaStat
                icon={TrendingUp}
                label="Observations"
                value={String(profile.total_observation_count)}
              />
              <MetaStat
                icon={Clock}
                label="Last Distillation"
                value={
                  profile.last_distillation
                    ? new Date(profile.last_distillation).toLocaleDateString()
                    : 'Never'
                }
              />
              <MetaStat
                icon={Activity}
                label="Schema"
                value={`v3.3`}
              />
              <MetaStat
                icon={Database}
                label="Created"
                value={new Date(profile.created_at).toLocaleDateString()}
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Tab Bar */}
      <motion.div
        {...fadeUp}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="border-b border-warm-200"
      >
        <nav className="-mb-px flex space-x-1">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition-all ${
                  isActive
                    ? 'border-copper-500 text-copper-700'
                    : 'border-transparent text-warm-500 hover:text-midnight-700 hover:border-warm-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'personality' && (
          <PersonalityTab
            personality={personality}
            commStyle={commStyle}
            cogStyle={cogStyle}
          />
        )}
        {activeTab === 'preferences' && (
          <PreferencesTab preferences={preferences} />
        )}
        {activeTab === 'domains' && (
          <DomainsTab
            domainPreferences={profile.domain_preferences}
            domainRegistry={profile.domain_registry}
          />
        )}
        {activeTab === 'raw' && (
          <RawDataTab profile={profile} />
        )}
      </motion.div>
    </div>
  )
}

// =============================================================================
// META STAT COMPONENT
// =============================================================================

function MetaStat({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-warm-50">
      <Icon className="w-4 h-4 text-copper-500 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-warm-500">{label}</div>
        <div className="text-sm font-medium text-midnight-800 truncate">{value}</div>
      </div>
    </div>
  )
}

// =============================================================================
// PERSONALITY TAB
// =============================================================================

function PersonalityTab({
  personality,
  commStyle,
  cogStyle,
}: {
  personality: BigFivePersonality
  commStyle: Record<string, TrackedValue>
  cogStyle: Record<string, TrackedValue>
}) {
  const hasPersonality = Object.keys(personality).length > 0
  const hasComm = Object.keys(commStyle).length > 0
  const hasCog = Object.keys(cogStyle).length > 0

  if (!hasPersonality && !hasComm && !hasCog) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Brain className="w-12 h-12 text-warm-300 mx-auto mb-3" />
          <p className="text-warm-500">No personality signals extracted yet.</p>
          <p className="text-warm-400 text-sm mt-1">
            Run a distillation to populate personality data.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      {/* Left column: Radar */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="w-5 h-5 text-copper-600" />
              Big Five Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            {hasPersonality ? (
              <PersonalityRadar
                data={personality}
                size="md"
                showLabels
                showConfidence
              />
            ) : (
              <div className="text-warm-400 text-sm py-8">
                No Big Five data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right column: Signal gauges */}
      <div className="lg:col-span-3 space-y-6">
        {/* Communication Style */}
        {hasComm && (
          <motion.div {...fadeUp} transition={{ duration: 0.4, delay: 0.1 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="w-5 h-5 text-copper-600" />
                  Communication Style
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SignalGauge label="Verbosity" value={commStyle.verbosity} leftLabel="Concise" rightLabel="Verbose" />
                <SignalGauge label="Formality" value={commStyle.formality} leftLabel="Casual" rightLabel="Formal" />
                <SignalGauge label="Directness" value={commStyle.directness} leftLabel="Indirect" rightLabel="Direct" />
                <SignalGauge label="Technical Depth" value={commStyle.technicalDepth || commStyle.technical_depth} leftLabel="Simple" rightLabel="Technical" />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Cognitive Preferences */}
        {hasCog && (
          <motion.div {...fadeUp} transition={{ duration: 0.4, delay: 0.15 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Zap className="w-5 h-5 text-copper-600" />
                  Cognitive Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SignalGauge label="Detail Preference" value={cogStyle.detailPreference || cogStyle.detail_preference} leftLabel="Overview" rightLabel="Detailed" />
                <SignalGauge label="Pace" value={cogStyle.pace} leftLabel="Slower" rightLabel="Faster" />
                <SignalGauge label="Structure" value={cogStyle.structurePreference || cogStyle.structure_preference} leftLabel="Free-form" rightLabel="Structured" />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Big Five Detail */}
        {hasPersonality && (
          <motion.div {...fadeUp} transition={{ duration: 0.4, delay: 0.2 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="w-5 h-5 text-copper-600" />
                  Big Five Traits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SignalGauge label="Openness" value={personality.openness} leftLabel="Conventional" rightLabel="Open" />
                <SignalGauge label="Conscientiousness" value={personality.conscientiousness} leftLabel="Flexible" rightLabel="Disciplined" />
                <SignalGauge label="Extraversion" value={personality.extraversion} leftLabel="Introverted" rightLabel="Extraverted" />
                <SignalGauge label="Agreeableness" value={personality.agreeableness} leftLabel="Challenging" rightLabel="Agreeable" />
                <SignalGauge label="Neuroticism" value={personality.neuroticism} leftLabel="Stable" rightLabel="Sensitive" />
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// PREFERENCES TAB
// =============================================================================

function PreferencesTab({ preferences }: { preferences: Record<string, unknown> }) {
  if (!preferences || Object.keys(preferences).length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <MessageSquare className="w-12 h-12 text-warm-300 mx-auto mb-3" />
          <p className="text-warm-500">No preferences extracted yet.</p>
          <p className="text-warm-400 text-sm mt-1">
            Preferences are discovered through conversation distillation.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="w-5 h-5 text-copper-600" />
          Extracted Preferences
        </CardTitle>
      </CardHeader>
      <CardContent>
        <PreferencesList preferences={preferences} />
      </CardContent>
    </Card>
  )
}

// =============================================================================
// DOMAINS TAB
// =============================================================================

function DomainsTab({
  domainPreferences,
  domainRegistry,
}: {
  domainPreferences: Record<string, any> | null
  domainRegistry: Record<string, any> | null
}) {
  const hasDomainPrefs = domainPreferences && Object.keys(domainPreferences).length > 0
  const hasRegistry = domainRegistry && Object.keys(domainRegistry).length > 0

  if (!hasDomainPrefs && !hasRegistry) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Layers className="w-12 h-12 text-warm-300 mx-auto mb-3" />
          <p className="text-warm-500">No domain preferences extracted yet.</p>
          <p className="text-warm-400 text-sm mt-1">
            Domain preferences capture taste in areas like music, dining, travel, etc.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Domain Registry */}
      {hasRegistry && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="w-5 h-5 text-copper-600" />
              Active Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.keys(domainRegistry!).map((domain) => (
                <Badge key={domain} variant="copper">
                  {domain.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Domain Preferences */}
      {hasDomainPrefs && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="w-5 h-5 text-copper-600" />
              Domain Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DomainPreferencesView data={domainPreferences!} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function DomainPreferencesView({ data }: { data: Record<string, any> }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="space-y-2">
      {Object.entries(data).map(([domain, prefs]) => {
        const isOpen = expanded.has(domain)
        const itemCount = typeof prefs === 'object' ? Object.keys(prefs || {}).length : 0

        return (
          <div key={domain} className="border border-warm-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggle(domain)}
              className="w-full flex items-center justify-between p-3 bg-warm-50 hover:bg-warm-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-warm-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-warm-500" />
                )}
                <span className="font-medium text-midnight-800 capitalize">
                  {domain.replace(/_/g, ' ')}
                </span>
              </div>
              <Badge variant="default" size="sm">
                {itemCount}
              </Badge>
            </button>
            {isOpen && (
              <div className="p-3 bg-white">
                <pre className="text-sm text-midnight-700 bg-warm-50 p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(prefs, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// =============================================================================
// RAW DATA TAB
// =============================================================================

type SignalTier = 'universal' | 'behavioral' | 'regulated' | 'pragmatic'

function RawDataTab({ profile }: { profile: any }) {
  const [activeTier, setActiveTier] = useState<SignalTier>('universal')

  const tiers: { key: SignalTier; label: string; icon: React.ElementType }[] = [
    { key: 'universal', label: 'Universal', icon: Brain },
    { key: 'behavioral', label: 'Behavioral', icon: Activity },
    { key: 'regulated', label: 'Regulated', icon: Shield },
    { key: 'pragmatic', label: 'Pragmatic', icon: Zap },
  ]

  const tierData: Record<SignalTier, Record<string, any>> = {
    universal: profile.signals_universal || {},
    behavioral: profile.signals_behavioral || {},
    regulated: profile.signals_regulated || {},
    pragmatic: profile.signals_pragmatic || {},
  }

  const currentData = tierData[activeTier]
  const isEmpty = !currentData || Object.keys(currentData).length === 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileJson className="w-5 h-5 text-copper-600" />
          Raw Signal Data
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Tier selector */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tiers.map((tier) => {
            const Icon = tier.icon
            const isActive = activeTier === tier.key
            const data = tierData[tier.key]
            const hasData = data && Object.keys(data).length > 0
            return (
              <button
                key={tier.key}
                onClick={() => setActiveTier(tier.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-copper-100 text-copper-800 border border-copper-300'
                    : hasData
                      ? 'bg-warm-50 text-midnight-700 border border-warm-200 hover:border-warm-300'
                      : 'bg-warm-50 text-warm-400 border border-warm-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tier.label}
              </button>
            )
          })}
        </div>

        {/* JSON display */}
        {isEmpty ? (
          <div className="text-center py-8 text-warm-500">
            No signal data in this tier.
          </div>
        ) : (
          <pre className="bg-warm-50 rounded-lg p-4 overflow-x-auto text-sm font-mono text-midnight-700 max-h-[600px] overflow-y-auto">
            {JSON.stringify(currentData, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  )
}
